import React, { useCallback, useMemo } from 'react';
import { componentTypes } from '@white-nl/instantsearch-components';

import Panel from '../ui/Panel';

type Category = {
  lft: number,
  slug: string,
  title: string,
  uri: string,
  children: Category[],
};

type Item = {
  label: string,
  value: string,
  isRefined: boolean,
  href?: string,
  data?: Item[] | null,
};

function HierarchicalMenuChildren({
  items,
  isLoading,
  mapItems,
}: {
  items: Item[],
  isLoading: boolean,
  mapItems: (items: Item[]) => Item[],
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ul className={`${isLoading ? '' : 'border-l border-pink-400 '}ps-4 mt-4.5 flex-col gap-y-2.5 hidden group-[.active]/filter-nav:flex `}>
      {mapItems(items).map((item) => (
        <li
          key={item.value}
          className={[
            'group/filter-subnav',
            item.isRefined && (item.data || []).length === 0 ? 'active' : null,
          ].filter(Boolean).join(' ')}
        >
          <a
            className={[
              'no-underline hover:text-pink-400 group-[.active]/filter-subnav:font-semibold group-[.active]/filter-subnav:text-pink-400',
              item.isRefined && (item.data || []).length > 0 ? 'font-semibold' : null,
            ].filter(Boolean).join(' ')}
            href={item.href}
          >
            {item.label}
          </a>
          <HierarchicalMenuChildren
            items={(item.data || [])}
            isLoading={isLoading}
            mapItems={mapItems}
          />
        </li>
      ))}
    </ul>
  );
}

export default function HierarchicalMenu({
  menu,
  menuSettings,
  categories,
  additionalData,
  createURL,
}: componentTypes.HierarchicalMenuPanelProps & { categories: Category[] }) {
  const flatCategories = useMemo(() => {
    const flatten = (cat: Category[], prefix: string = ''): Record<string, Category> => cat
      .reduce((acc, category) => {
        const key = prefix ? `${prefix}/${category.slug}` : category.uri;
        acc[key] = category;
        if (category.children?.length > 0) {
          Object.assign(acc, flatten(category.children, key));
        }
        return acc;
      }, {} as Record<string, Category>);

    return flatten(categories);
  }, [categories]);

  const mapItems = useCallback((items: Item[]) => (
    (items || [])
      .sort((a, b) => (flatCategories[a.value]?.lft || 999) - (flatCategories[b.value]?.lft || 999))
      .map((item) => {
        const label = flatCategories[item.value]?.title
          || flatCategories[item.label]?.title
          || '\u200E';
        const value = flatCategories[item.value]?.uri
          || flatCategories[item.label]?.uri
          || item.value;

        const currentUrl = new URL(createURL ? createURL(value) : window.location.href);
        const newUrl = new URL(`${additionalData.baseUrl || '/'}${value}/`);
        const params = new URLSearchParams(currentUrl.search);
        params.delete('page'); // Remove the "page" parameter
        newUrl.search = params.toString();

        return ({
          ...item,
          label,
          value,
          href: newUrl.toString(),
        });
      })
  ), [flatCategories, additionalData.baseUrl, createURL]);

  const { canRefine } = menu;
  const { attributes, separator, panelSettings } = menuSettings;

  const header = `${panelSettings?.header || attributes.join(separator)}`;

  if (!canRefine) {
    return null;
  }

  return (
    <Panel header={header}>
      <ul className="text-base flex flex-col gap-y-4 text-gray-850">
        {mapItems(menu.items[0]?.data || []).map((item) => (
          <li key={item.value} className={`${item.isRefined ? 'active text-gray-900 ' : ''}group/filter-nav`}>
            <a
              className="no-underline hover:text-pink-400 group-[.active]/filter-nav:font-semibold"
              href={item.href}
            >
              {item.label}
            </a>
            <HierarchicalMenuChildren
              items={(item.data || [])}
              isLoading={Object.keys(flatCategories).length === 0}
              mapItems={mapItems}
            />
          </li>
        ))}
      </ul>
    </Panel>
  );
}
